import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { MaskedLines } from '@/components/MaskedLines';
import { PLANTS, plateImg, STATS } from '@/lib/databank';

const FloatingPlate = ({ plant, className, rotate = 0, parallax, delay = 0.9 }) => {
    const reduce = useReducedMotion();
    return (
        <motion.div style={reduce ? undefined : { y: parallax }} className={`pointer-events-none absolute ${className}`}>
            <motion.div
                initial={reduce ? false : { opacity: 0, rotate: rotate + 8, scale: 0.92 }}
                animate={{ opacity: 1, rotate, scale: 1 }}
                transition={{ duration: 1.4, delay, ease: [0.16, 1, 0.3, 1] }}
                className="border border-line bg-paper p-3"
            >
                <img src={plateImg(plant)} alt={plant.botanical_name} className="plate-img w-full" />
                <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-mute">
                    Plate {String(plant.plant_no).padStart(2, '0')}
                </p>
            </motion.div>
        </motion.div>
    );
};

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
    const yFar = useTransform(scrollYProgress, [0, 1], [0, -140]);
    const yNear = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const yText = useTransform(scrollYProgress, [0, 1], [0, 90]);

    const scrollToChapters = (e) => {
        e.preventDefault();
        if (window.__lenis) window.__lenis.scrollTo('#chapters', { offset: -70 });
        else document.getElementById('chapters')?.scrollIntoView();
    };

    return (
        <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden border-b border-line">
            {/* floating parallax plates */}
            <FloatingPlate plant={PLANTS[56]} className="right-[6%] top-[12%] z-0 hidden w-56 md:block lg:w-72" rotate={4} parallax={yFar} delay={1.0} />
            <FloatingPlate plant={PLANTS[12]} className="right-[26%] top-[46%] z-0 hidden w-44 lg:block" rotate={-5} parallax={yNear} delay={1.2} />
            <FloatingPlate plant={PLANTS[71]} className="right-[4%] top-[54%] z-0 hidden w-36 md:block" rotate={2} parallax={yFar} delay={1.35} />

            <motion.div style={{ y: yText }} className="relative z-10 mx-auto w-full max-w-[1500px] px-5 pb-10 pt-28 md:px-10">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-terra"
                    data-testid="hero-eyebrow"
                >
                    Data Bank — Amravati Region · Maharashtra
                </motion.p>

                <h1 className="font-serif leading-[0.88] tracking-[-0.03em] text-ink" data-testid="hero-headline">
                    <MaskedLines
                        lines={['Asteraceae']}
                        lineClassName="italic text-[16vw] md:text-[12.5vw] text-forest"
                        delay={0.35}
                    />
                    <MaskedLines
                        lines={['of Western Vidarbha']}
                        lineClassName="text-[8.5vw] md:text-[6.5vw] text-ink"
                        delay={0.55}
                    />
                </h1>

                <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                    <div className="max-w-md">
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
                        className="text-sm leading-relaxed text-ink/70 md:text-base"
                    >
                        Seventy-two species of the daisy family documented across Western
                        Vidarbha — nomenclature, habit, native range and economic importance,
                        each with a hand-rendered illustration plate of the flowering twig.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-6 max-w-md font-mono text-[10px] uppercase leading-loose tracking-[0.22em] text-mute"
                        data-testid="hero-author-credit"
                    >
                        <span className="text-ink">Mayuri R. Kathalkar</span><br />
                        <span className="text-ink">Prof. Manjusha R. Wath</span>
                    </motion.p>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
                        className="flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/gallery"
                            data-testid="hero-browse-cta"
                            className="group inline-flex items-center gap-3 border border-forest bg-forest px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-paper transition-colors duration-300 hover:bg-terra hover:border-terra"
                        >
                            Browse the collection
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <a
                            href="#chapters"
                            onClick={scrollToChapters}
                            data-testid="hero-manifesto-cta"
                            className="group inline-flex items-center gap-3 px-2 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink"
                        >
                            <span className="u-line">The manifesto</span>
                            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
                        </a>
                    </motion.div>
                </div>

                {/* stats strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.5 }}
                    className="mt-14 grid grid-cols-2 border-t border-line md:grid-cols-4"
                    data-testid="hero-stats"
                >
                    {[
                        [STATS.species, 'Species'],
                        [STATS.tribes, 'Tribes'],
                        ['01', 'Family'],
                        [STATS.plates, 'Plates'],
                    ].map(([n, l], i) => (
                        <div key={l} className={`flex items-baseline gap-3 py-5 ${i > 0 ? 'md:border-l md:border-line md:pl-8' : ''} ${i % 2 === 1 ? 'border-l border-line pl-6 md:pl-8' : ''}`}>
                            <span className="font-serif text-4xl italic text-ink md:text-5xl">{n}</span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mute">{l}</span>
                        </div>
                    ))}
                </motion.div>
            </motion.div>
        </section>
    );
};
